if (!customElements.get('bst-product-financing')) {
  customElements.define(
    'bst-product-financing',
    class BstProductFinancing extends HTMLElement {
      constructor() {
        super();
        this.observer = null;
        this.variantChangeUnsubscriber = null;
        this.retryTimer = null;
      }

      connectedCallback() {
        this.display = this.querySelector('[data-financing-display]');
        this.paymentTerms = this.querySelector('shopify-payment-terms');
        this.sectionId = this.dataset.sectionId;

        if (!this.paymentTerms) {
          this.showFallback();
          return;
        }

        this.observePaymentTerms();
        this.updateDisplay();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId !== this.getSectionId()) return;
          this.scheduleUpdate();
        });

        this.querySelector('input[name="id"]')?.addEventListener('change', () => this.scheduleUpdate());

        [100, 500, 1500].forEach((delay) => this.scheduleUpdate(delay));
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.variantChangeUnsubscriber?.();
        clearTimeout(this.retryTimer);
      }

      getSectionId() {
        return this.sectionId || this.closest('product-info')?.dataset.section;
      }

      observePaymentTerms() {
        this.observer = new MutationObserver(() => this.updateDisplay());
        this.observer.observe(this.paymentTerms, {
          attributes: true,
          attributeFilter: ['shopify-meta', 'variant-id'],
        });
      }

      scheduleUpdate(delay = 50) {
        clearTimeout(this.retryTimer);
        this.retryTimer = setTimeout(() => this.updateDisplay(), delay);
      }

      updateDisplay() {
        const amount = this.getMonthlyAmount();
        if (amount) {
          this.renderAmount(amount);
          return;
        }

        this.showFallback();
      }

      getMonthlyAmount() {
        const fromShadow = this.getAmountFromShadowDom();
        if (fromShadow) return fromShadow;

        const metaRaw = this.paymentTerms?.getAttribute('shopify-meta');
        const variantId = this.paymentTerms?.getAttribute('variant-id');
        if (!metaRaw || !variantId) return null;

        try {
          return this.calculateFromMeta(JSON.parse(metaRaw), variantId);
        } catch {
          return null;
        }
      }

      getAmountFromShadowDom() {
        const bold = this.paymentTerms?.shadowRoot?.querySelector(
          '#shopify-installments-content .font-bold, .font-bold'
        );
        return bold?.textContent?.trim() || null;
      }

      calculateFromMeta(meta, variantId) {
        const variant = meta.variants?.find((entry) => String(entry.id) === String(variantId));
        if (!variant?.eligible || !variant?.available) return null;

        const price = this.parseMoney(variant.full_price);
        const plan = meta.financing_plans?.find((entry) => {
          return price >= this.parseMoney(entry.min_price) && price <= this.parseMoney(entry.max_price);
        });
        if (!plan?.terms?.length) return null;

        const interestTerms = plan.terms.filter((term) => term.loan_type === 'interest');
        if (interestTerms.length) {
          const term = interestTerms.reduce((longest, current) =>
            current.installments_count > longest.installments_count ? current : longest
          );
          return this.formatMoney(
            this.calculateMonthlyPayment(price, term.apr, term.installments_count),
            meta.currency_code
          );
        }

        const splitTerms = plan.terms.filter((term) => term.loan_type === 'split_pay');
        if (splitTerms.length) {
          const term = splitTerms.reduce((longest, current) =>
            current.installments_count > longest.installments_count ? current : longest
          );
          return this.formatMoney(price / term.installments_count, meta.currency_code);
        }

        return null;
      }

      parseMoney(value) {
        return parseFloat(String(value).replace(/[^0-9.]/g, '')) || 0;
      }

      calculateMonthlyPayment(principal, apr, months) {
        if (!months) return principal;
        if (!apr) return principal / months;

        const rate = apr / 100 / 12;
        const factor = Math.pow(1 + rate, months);
        return (principal * rate * factor) / (factor - 1);
      }

      formatMoney(amount, currencyCode = 'USD') {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      }

      renderAmount(amount) {
        if (!this.display) return;

        this.display.innerHTML = `From <strong>${amount}/mo</strong> financing`;
        this.display.hidden = false;
        this.hidden = false;
      }

      showFallback() {
        const fallback = this.dataset.fallbackHtml;
        if (fallback && this.display) {
          this.display.innerHTML = fallback;
          this.display.hidden = false;
          this.hidden = false;
          return;
        }

        if (this.display) this.display.hidden = true;
        this.hidden = true;
      }
    }
  );
}
